import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MessageEnum } from '../../../core/utils/message.enum';
import { ZorroTableComponent } from '../../common/custom-table/zorro-table.component';
import { DropdownWithSectionsComponent } from '../../common/dropdown-with-sections/dropdown-with-sections.component';

@Component({
  selector: 'app-create-device-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NzSwitchModule, ZorroTableComponent, DropdownWithSectionsComponent],
  templateUrl: './create-device-modal.component.html',
  styleUrl: './create-device-modal.component.scss',
})
export class CreateDeviceModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly toaster = inject(ToasterService);

  @Input() projectId: string = '';
  @Input() isAgenticView: boolean = false;
  @Input() device: any = null;

  get isEditMode(): boolean {
    return !!this.device;
  }

  isActive = false;
  isLoading = false;
  agents: any[] = [];
  agentSections: any[] = [];
  agentConfig: any = {
    placeholderText: 'Select execution engine',
    iconType: 'assetImage' as const
  };
  remoteOptionsEnabled = true;
  remoteOptionsExpanded = true;
  originalDeviceName: string = '';

  remoteOptionsTableData: any = {
    columns: [
      {
        key: 'key',
        display_name: 'Key',
        cellStyles: { 'font-size': '13px' },
        headerStyles: { background: '#242933', 'font-weight': '600', width: '40%', color: '#fff', borderRadius: '6px 0 0 0' },
        _editConfig: { type: 'input', placeholder: 'Enter key' },
      },
      {
        key: 'value',
        display_name: 'Value',
        cellStyles: { 'font-size': '13px' },
        headerStyles: { background: '#242933', 'font-weight': '600', color: '#fff' },
        _editConfig: { type: 'input', placeholder: 'Enter value' },
      },
      {
        key: 'edit',
        display_name: '',
        cellStyles: { 'font-size': '13px' },
        noBorderRight: true,
        headerStyles: { background: '#242933', 'font-weight': '600', width: '3%', color: '#fff' },
        _config: { type: 'svg-editor', value: 'assets/images/virtusa-icons/edit-light.svg', isClickable: 'true', actionType: 'edit' },
        _editConfig: { type: 'svg-editor', actionType: 'add', value: 'assets/images/virtusa-icons/tick.svg', isClickable: 'true' },
      },
      {
        key: 'delete',
        display_name: '',
        cellStyles: { 'font-size': '13px' },
        noBorderRight: true,
        headerStyles: { background: '#242933', 'font-weight': '600', width: '3%', color: '#fff', borderRadius: '0 6px 0 0' },
        _config: { type: 'svg-editor', value: 'assets/images/virtusa-icons/delete-red.svg', isClickable: 'true', actionType: 'delete' },
        _editConfig: { type: 'svg-editor', value: 'assets/images/virtusa-icons/cross.svg', isClickable: 'true', actionType: 'cancel' },
      },
    ],
    rows: [],
  };

  form: any = {
    environment: 'LOCAL',
    name: '',
    serialNumber: '',
    platform: 'ANDROID',
    platformVersion: '',
    automationPort: '',
    host: '',
    agentId: '',
    simulator: false,
    deviceType: 'EMULATOR',
    remoteEnvironment: '',
    username: '',
    accessKey: '',
  };

  get remoteOptionsCount(): number {
    return this.remoteOptionsTableData.rows.filter((r: any) => !r.isDraft).length;
  }

  addRemoteOption(): void {
    if (this.remoteOptionsTableData.rows[0]?.isDraft) return;
    this.remoteOptionsTableData.rows = [
      { key: '', value: '', isRowEditable: true, isDraft: true },
      ...this.remoteOptionsTableData.rows,
    ];
  }

  handleRemoteOptionsAction(event: any): void {
    switch (event?.actionType) {
      case 'add': {
        const row = event?.data?.row;
        if (row?.key?.trim()) {
          this.remoteOptionsTableData.rows.shift();
          this.remoteOptionsTableData.rows = [
            { ...row, isRowEditable: false, isDraft: false },
            ...this.remoteOptionsTableData.rows,
          ];
        }
        break;
      }
      case 'cancel':
        this.remoteOptionsTableData.rows.shift();
        break;
      case 'edit': {
        const targetKey = event?.data?.row?.key;
        this.remoteOptionsTableData.rows = this.remoteOptionsTableData.rows.map((r: any) =>
          r.key === targetKey ? { ...r, isRowEditable: true, isDraft: true } : r
        );
        break;
      }
      case 'delete': {
        const targetKey = event?.data?.row?.key;
        this.remoteOptionsTableData.rows = this.remoteOptionsTableData.rows.filter(
          (r: any) => r.key !== targetKey
        );
        break;
      }
    }
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.restService.getAgents(this.projectId).subscribe({
        next: (res: any) => {
          this.agents = (res?.data ?? []).map((a: any) => ({
            value: a.id,
            label: a.name || a.agentName || a.id,
          }));
          this.buildAgentSections(res?.data ?? []);
          
          // Set device data after agentSections is built
          if (this.device) {
            this.populateDeviceData();
          }
        },
        error: () => {
          // Even on error, populate device data
          if (this.device) {
            this.populateDeviceData();
          }
        },
      });
    } else if (this.device) {
      // No projectId but have device data
      this.populateDeviceData();
    }
  }

  private populateDeviceData(): void {
    const d = this.device;
    this.form.environment = d.environment || 'LOCAL';
    this.originalDeviceName = d.name || '';
    this.form.name = d.name || '';
    this.form.serialNumber = d.serialNumber || '';
    this.form.platform = (d.platform || 'ANDROID').toUpperCase();
    this.form.platformVersion = d.platformVersion || '';
    this.form.automationPort = d.automationPort ? String(d.automationPort) : '';
    this.form.host = d.automationHost || '';
    this.form.agentId = d.agentId || 'onDemand';
    this.form.simulator = d.simulator || false;
    this.form.deviceType = d.deviceType || 'EMULATOR';
    this.form.remoteEnvironment = d.remoteEnvironment || '';
    this.form.username = d.credentials?.username || '';
    this.form.accessKey = d.credentials?.accessKey || '';
    this.populateRemoteOptions(d.config);
  }

  private populateRemoteOptions(config: any): void {
    if (config && typeof config === 'object' && !Array.isArray(config)) {
      const entries = Object.entries(config);
      if (entries.length) {
        this.remoteOptionsTableData.rows = entries.map(([key, value]) => ({
          key,
          value,
          isRowEditable: false,
          isDraft: false,
        }));
      }
    }
  }

  get isLocal(): boolean {
    return this.form.environment === 'LOCAL';
  }

  get isFormValid(): boolean {
    if (!this.form.name || !this.form.platform || !this.form.platformVersion || !this.form.agentId) {
      return false;
    }
    if (this.isLocal) {
      return !!(this.form.serialNumber && this.form.automationPort && this.form.host);
    }
    return !!(this.form.remoteEnvironment && this.form.username && this.form.accessKey);
  }

  selectPlatform(platform: 'ANDROID' | 'IOS'): void {
    this.form.platform = platform;
  }

  buildAgentSections(agents: any[]): void {
    const localAgents: any[] = [];
    const cloudAgents: any[] = [];

    agents.forEach((agent: any) => {
      const currentAgent = {
        label: agent.name || agent.agentName || agent.id,
        icon: agent.status === 'OFFLINE' ? 'assets/images/agent/agent-dead.svg' : 'assets/images/agent/agent-alive.svg',
        value: agent.id,
        agentType: agent.type,
      };
      agent.type === 'LOCAL' ? localAgents.push(currentAgent) : cloudAgents.push(currentAgent);
    });

    // On Demand agent
    this.agentSections.push({
      label: '',
      type: 'onDemand',
      options: [
        {
          label: 'On Demand',
          icon: 'assets/images/agent/agent-alive.svg',
          value: 'onDemand',
          agentType: 'CLUSTER'
        }
      ]
    });

    if (localAgents.length) {
      this.agentSections.push({
        label: 'Local Execution Engines',
        options: localAgents
      });
    }
    if (cloudAgents.length) {
      this.agentSections.push({
        label: 'Cloud Based Execution Engines',
        options: cloudAgents
      });
    }
  }

  onAgentSelect(agent: any): void {
    this.form.agentId = agent.value;
  }

  buildPrompt(): string {
    const platformLabel = this.form.platform === 'ANDROID' ? 'Android' : 'iOS';
    const environmentLabel = this.form.environment === 'LOCAL' ? 'Local' : 'Remote';
    const simulatorOrEmulator = this.form.simulator ? 'SIMULATOR' : 'EMULATOR';
    const deviceType = this.isLocal ? simulatorOrEmulator : 'EMULATOR';
    const action = this.isEditMode ? 'Update the Mobile Device' : 'Configure a Mobile Device';
    const agentLabel = this.agents.find((a: any) => a.value === this.form.agentId)?.label || this.form.agentId;
    
    const lines: string[] = [
      action,
      `environment: '${environmentLabel}'`,
      `Name : '${this.isEditMode ? this.originalDeviceName : this.form.name}'`,
      ...(this.isEditMode ? [`New name : '${this.form.name}'`] : []),
      `platform: '${platformLabel}'`,
      `Platform version: '${this.form.platformVersion}'`,
      `Device Type: '${deviceType}'`,
      `Agent Name: '${agentLabel}'`
    ];

    if (this.isLocal) {
      lines.push(`Serial Number: '${this.form.serialNumber}'`);
      lines.push(`Automation Host: '${this.form.host}'`);
      lines.push(`Automation Port: '${this.form.automationPort}'`);
    } else {
      lines.push(`Remote Environment: '${this.form.remoteEnvironment}'`);
      const savedOptions = this.remoteOptionsTableData.rows.filter((r: any) => !r.isDraft);
      if (savedOptions.length) {
        const configStr = savedOptions.map((r: any) => `${r.key}=${r.value}`).join(',');
        lines.push(`Device Config: '${configStr}'`);
      }
    }

    return lines.join('\n');
  }

  submit(): void {
    if (!this.isFormValid) return;

    if (this.isAgenticView) {
      const result: any = { prompt: this.buildPrompt() };
      if (!this.isLocal) {
        result.serviceSecrets = {
          remote_username: this.form.username,
          remote_access_key: this.form.accessKey,
        };
      }
      this.activeModal.close(result);
      return;
    }

    const simulatorOrEmulator = this.form.simulator ? 'SIMULATOR' : 'EMULATOR';
    const payload: any = {
      name: this.form.name,
      platform: this.form.platform,
      platformVersion: this.form.platformVersion,
      environment: this.form.environment,
      agentId: this.form.agentId,
      deviceType: this.isLocal ? simulatorOrEmulator : 'EMULATOR',
      simulator: this.form.simulator,
    };

    if (this.isLocal) {
      payload.serialNumber = this.form.serialNumber;
      payload.automationPort = Number(this.form.automationPort);
      payload.automationHost = this.form.host;
    } else {
      payload.remoteEnvironment = this.form.remoteEnvironment;
      payload.credentials = {
        username: this.form.username,
        accessKey: this.form.accessKey,
      };
      const savedOptions = this.remoteOptionsTableData.rows.filter((r: any) => !r.isDraft);
      payload.config = savedOptions.reduce((acc: any, r: any) => {
        acc[r.key] = r.value;
        return acc;
      }, {});
    }

    this.isLoading = true;
    const apiCall = this.isEditMode
      ? this.restService.editDevice(this.projectId, this.device.id, payload)
      : this.restService.addDevice(this.projectId, payload);

    apiCall.subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.toaster.pop({ type: 'success', text: this.isEditMode ? MessageEnum.DEVICE_UPDATE_SUCCESS : MessageEnum.DEVICE_CREATE_SUCCESS });
        this.activeModal.close(res?.data);
      },
      error: () => {
        this.isLoading = false;
        this.toaster.pop({ type: 'error', text: this.isEditMode ? MessageEnum.DEVICE_UPDATE_FAILURE : MessageEnum.DEVICE_CREATE_FAILURE });
      },
    });
  }
}
